"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X, ChevronDown, User, LogOut, Lock } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthUser } from "@/hooks/useAuthUser";
import { signOut, useSession } from "next-auth/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNextAuthReturnUrl } from "@/hooks/useReturnUrl";
import { useRouter } from "next/navigation";
import { destroySession } from "@/utils/auth";
import { routes } from "@/utils/routes";

// Navigation links definition
const NAV_LINKS = [
  { href: routes.web.dashboard.home, label: "Home" },
  { href: routes.web.users.tickets, label: "My Tickets" },
  { href: routes.web.contact, label: "Contact" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const path = usePathname();
  const user = useAuthUser();
  const returnUrl = useNextAuthReturnUrl();
  const { data: session } = useSession();
  const router = useRouter();

  // Handle returnUrl redirect
  useEffect(() => {
    if (returnUrl) {
      document.cookie = "NextAuthReturnUrl=; path=/; max-age=0";
      router.push(returnUrl);
    }
  }, [returnUrl, router]);

  // Handle scroll effects
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  const logout = useCallback(async () => {
    await destroySession();
    if (session) {
      await signOut({ callbackUrl: "/auth/login" });
    } else {
      window.location.href = "/auth/login";
    }
  }, [session]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled
            ? "py-2 bg-black/95 backdrop-blur-lg shadow-lg border-b border-zinc-800/50"
            : "py-4 bg-gradient-to-b from-black/90 to-transparent"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="relative z-20 group">
              <div className="overflow-hidden">
                <Image
                  src="/logo.svg"
                  alt="First In"
                  className={`transition-all duration-300 transform group-hover:scale-105 ${
                    scrolled ? "h-9 w-auto" : "h-11 w-auto"
                  }`}
                  width={48}
                  height={48}
                  priority
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6">
              <div className="flex space-x-1">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`relative text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 overflow-hidden ${
                      path === link.href
                        ? "text-white"
                        : "hover:text-white text-gray-300"
                    }`}
                  >
                    <span className="relative z-10">{link.label}</span>
                    {path === link.href && (
                      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-zinc-500 to-zinc-500"></span>
                    )}
                    <span
                      className={`absolute inset-0 bg-gradient-to-r from-zinc-500/20 to-zinc-500/20 rounded-md opacity-0 transition-opacity duration-300 ${
                        path === link.href ? "opacity-100" : "hover:opacity-100"
                      }`}
                    ></span>
                  </Link>
                ))}
              </div>

              <div className="flex items-center space-x-4 ml-4 pl-6 border-l border-gray-700">
                {user?.id ? (
                  <UserMenu user={user} logout={logout} />
                ) : (
                  <Link href="/auth/login">
                    <Button className="bg-white text-black border-none">
                      Login
                    </Button>
                  </Link>
                )}
              </div>
            </nav>

            {/* Mobile Toggle Button */}
            <div className="flex lg:hidden items-center z-20">
              <Button
                variant="outline"
                size="icon"
                className={`text-white ${
                  scrolled
                    ? "bg-zinc-500 border-zinc-500 hover:bg-zinc-600"
                    : "bg-black/70 border-gray-700 hover:bg-black/90"
                }`}
                onClick={toggleMobileMenu}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Off-Canvas Mobile Menu - Separate from Header */}
      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-50 lg:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileMenu}
      />

      <div
        className={`fixed top-0 right-0 z-50 lg:hidden h-full w-4/5 max-w-sm bg-zinc-900 shadow-xl transform transition-transform duration-300 ease-out ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <Link href="/" onClick={closeMobileMenu}>
              <Image
                src="/logo.svg"
                alt="First In"
                className="h-8 w-auto"
                width={32}
                height={32}
                priority
              />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10 rounded-full"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </Button>
          </div>

          {/* Menu Content */}
          <div className="flex flex-col h-full overflow-auto">
            {/* User Profile (if logged in) */}
            {user?.id && (
              <div className="p-4 border-b border-zinc-800">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-12 w-12 rounded-full border-2 border-zinc-500">
                    <AvatarImage
                      src={user?.image ?? "/logo.svg"}
                      alt={user?.name ?? "User"}
                    />
                    <AvatarFallback className="bg-zinc-900 text-white">
                      {user?.name?.slice(0, 2).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {user?.name}
                    </p>
                    <p className="text-gray-400 text-sm truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <nav className="py-2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`flex items-center px-4 py-3 text-base font-medium ${
                    path === link.href
                      ? "bg-gradient-to-r from-zinc-500 via-zinc-500 to-zinc-500 text-white"
                      : "text-gray-300 hover:bg-zinc-800 hover:text-white"
                  }`}
                  onClick={closeMobileMenu}
                >
                  {link.label}
                </Link>
              ))}

              {/* Login Button (if not logged in) */}
              {!user?.id && (
                <div className="px-4 py-3">
                  <Link
                    href="/auth/login"
                    className="flex items-center justify-center w-full py-3 bg-gradient-to-r from-white/10 via-white/20 to-white/10 hover:from-white/20 hover:to-white/20 text-white rounded-md font-medium"
                    onClick={closeMobileMenu}
                  >
                    Login
                  </Link>
                </div>
              )}
            </nav>

            {/* Account Section (if logged in) */}
            {user?.id && (
              <div className="mt-auto">
                <div className="px-4 py-2 border-t border-zinc-800">
                  <p className="px-1 text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                    Account
                  </p>
                  <Link
                    href="/users/profile"
                    className="flex items-center px-4 py-3 text-gray-300 hover:bg-zinc-800 hover:text-white"
                    onClick={closeMobileMenu}
                  >
                    <User className="mr-3 h-5 w-5" />
                    Profile
                  </Link>
                  <Link
                    href="/users/profile#change-password"
                    className="flex items-center px-4 py-3 text-gray-300 hover:bg-zinc-800 hover:text-white"
                    onClick={closeMobileMenu}
                  >
                    <Lock className="mr-3 h-5 w-5" />
                    Change Password
                  </Link>
                  <button
                    onClick={() => {
                      closeMobileMenu();
                      logout();
                    }}
                    className="flex items-center w-full px-4 py-3 text-red-400 hover:bg-zinc-800"
                  >
                    <LogOut className="mr-3 h-5 w-5" />
                    Logout
                  </button>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="p-4 mt-auto border-t border-zinc-800">
              <p className="text-zinc-500 text-xs text-center">
                © {new Date().getFullYear()} First In. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function UserMenu({ user, logout }: { user: any; logout: any }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center space-x-2 px-3 py-2 hover:bg-white/10 backdrop-blur-md rounded-full"
        >
          <Avatar className="h-8 w-8 rounded-full border-2 border-zinc-500 overflow-hidden">
            <AvatarImage
              src={user?.image ?? "/logo.svg"}
              alt={user?.name ?? "User"}
              className="object-cover"
            />
            <AvatarFallback className="bg-zinc-900 text-white">
              {user?.name?.slice(0, 2).toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-white max-w-[100px] truncate">
            {user?.name}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 bg-zinc-900 border border-zinc-800 text-white"
      >
        <div className="px-4 py-3 border-b border-zinc-800">
          <p className="text-sm font-medium">{user?.name}</p>
          <p className="text-xs text-gray-400 truncate">{user?.email}</p>
        </div>
        <DropdownMenuItem className="focus:bg-zinc-800">
          <Link href="/users/profile" className="flex items-center w-full">
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem className="focus:bg-zinc-800">
          <Link
            href="/users/profile#change-password"
            className="flex items-center w-full"
          >
            <Lock className="mr-2 h-4 w-4" />
            <span>Change Password</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem
          className="focus:bg-zinc-800 text-red-400"
          onClick={logout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
